// apps/web/src/api/commercial-proposals.ts
//
// @aiPurpose typed wrappers around `/commercial-proposals/*` (#1198). All
//   callers route through `request<T>` / `requestBlob` so the coordinated
//   401 retry / `ThrottleError` / `ApiClientError` surface is preserved.
//   Responses are validated with the shared Zod schemas from
//   `@magimp/shared` so a wire-shape drift surfaces as a ZodError rather
//   than silently mis-typed data.
import { type ZodType } from 'zod';
import {
  CommercialProposalDetailResponseSchema,
  CommercialProposalListResponseSchema,
  CommercialProposalViewsResponseSchema,
  StorageDownloadUrlResponseSchema,
  type CommercialProposalDetailResponse,
  type CommercialProposalListResponse,
  type CommercialProposalViewsResponse,
  type CreateCommercialProposalInput,
  type PatchCommercialProposalInput,
  type StorageDownloadUrlResponse,
} from '@magimp/shared';

import { request, requestBlob, type BinaryDownloadResult } from './client.js';

export function listCommercialProposals(
  opts: { signal?: AbortSignal } = {},
): Promise<CommercialProposalListResponse> {
  return request<CommercialProposalListResponse>(
    '/commercial-proposals',
    { method: 'GET' },
    opts.signal ? { signal: opts.signal } : {},
    CommercialProposalListResponseSchema as unknown as ZodType<CommercialProposalListResponse>,
  );
}

export function getCommercialProposal(
  id: string,
  opts: { signal?: AbortSignal } = {},
): Promise<CommercialProposalDetailResponse> {
  return request<CommercialProposalDetailResponse>(
    `/commercial-proposals/${encodeURIComponent(id)}`,
    { method: 'GET' },
    opts.signal ? { signal: opts.signal } : {},
    CommercialProposalDetailResponseSchema as unknown as ZodType<CommercialProposalDetailResponse>,
  );
}

export function createCommercialProposal(
  input: CreateCommercialProposalInput,
): Promise<CommercialProposalDetailResponse> {
  return request<CommercialProposalDetailResponse>(
    '/commercial-proposals',
    {
      method: 'POST',
      body: JSON.stringify(input),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CommercialProposalDetailResponseSchema as unknown as ZodType<CommercialProposalDetailResponse>,
  );
}

export function patchCommercialProposal(
  id: string,
  patch: PatchCommercialProposalInput,
): Promise<CommercialProposalDetailResponse> {
  return request<CommercialProposalDetailResponse>(
    `/commercial-proposals/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(patch),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CommercialProposalDetailResponseSchema as unknown as ZodType<CommercialProposalDetailResponse>,
  );
}

/**
 * @aiPurpose presigned GET for the SAVED product photo (#1207). The backend
 *   resolves the proposal's currently-bound `content.photoObjectKey` itself
 *   (404 when none), so this previews the persisted photo only — never an
 *   unsaved, freshly-uploaded key. Used by the draft editor and the
 *   read-only drawer.
 */
export function requestCommercialProposalPhotoDownloadUrl(
  proposalId: string,
): Promise<StorageDownloadUrlResponse> {
  return request<StorageDownloadUrlResponse>(
    `/commercial-proposals/${encodeURIComponent(proposalId)}/photo-download-url`,
    { method: 'POST' },
    {},
    StorageDownloadUrlResponseSchema,
  );
}

export function publishCommercialProposal(id: string): Promise<CommercialProposalDetailResponse> {
  return request<CommercialProposalDetailResponse>(
    `/commercial-proposals/${encodeURIComponent(id)}/publish`,
    { method: 'POST' },
    {},
    CommercialProposalDetailResponseSchema as unknown as ZodType<CommercialProposalDetailResponse>,
  );
}

export function revokeCommercialProposal(id: string): Promise<CommercialProposalDetailResponse> {
  return request<CommercialProposalDetailResponse>(
    `/commercial-proposals/${encodeURIComponent(id)}/revoke`,
    { method: 'POST' },
    {},
    CommercialProposalDetailResponseSchema as unknown as ZodType<CommercialProposalDetailResponse>,
  );
}

export function getCommercialProposalViews(
  id: string,
  opts: { signal?: AbortSignal } = {},
): Promise<CommercialProposalViewsResponse> {
  return request<CommercialProposalViewsResponse>(
    `/commercial-proposals/${encodeURIComponent(id)}/views`,
    { method: 'GET' },
    opts.signal ? { signal: opts.signal } : {},
    CommercialProposalViewsResponseSchema as unknown as ZodType<CommercialProposalViewsResponse>,
  );
}

/**
 * @aiPurpose download the per-proposal PDF export as a Blob plus the
 *   Content-Disposition filename. The button caller owns the
 *   download-trigger side effect (object URL + anchor click + revoke);
 *   isolating it here keeps the API helper free of DOM coupling. Wired for
 *   the Phase B download button.
 */
export function downloadCommercialProposalPdf(
  id: string,
  opts: { signal?: AbortSignal } = {},
): Promise<BinaryDownloadResult> {
  return requestBlob(`/commercial-proposals/${encodeURIComponent(id)}/export.pdf`, opts);
}
